import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { unwrapResult } from '@/shared/utils/unwrapResult'
import { resultQueryFn, resultMutationFn } from '@/shared/utils/queryHelpers'
import { queryKeys } from '@/shared/utils/queryKeys'
import {
  submitGeneration,
  getGenerationStatus,
  cancelGeneration,
  getAvailableModels,
  getSystemStats,
} from './api'
import type { GenerationRequest, GenerationResponse, ResourceType } from './types'

export function useAvailableModels(resourceType: ResourceType) {
  return useQuery({
    queryKey: queryKeys.generation.models(resourceType),
    queryFn: async () => {
      const result = await getAvailableModels(resourceType)
      return unwrapResult(result).models
    },
    staleTime: 60_000,
  })
}

export function useSystemStats() {
  return useQuery({
    queryKey: queryKeys.generation.systemStats,
    queryFn: async () => {
      const result = await getSystemStats()
      return unwrapResult(result).system_stats
    },
    staleTime: 10_000,
  })
}

export function useSubmitGeneration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: resultMutationFn<GenerationRequest, GenerationResponse>(submitGeneration),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.generation.list })
    },
  })
}

export function useCancelGeneration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: resultMutationFn<string, GenerationResponse>(cancelGeneration),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.generation.detail(id) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.generation.list })
    },
  })
}

export function useGenerationStatus(id: string | null) {
  return useQuery({
    queryKey: queryKeys.generation.detail(id ?? ''),
    queryFn: async () => {
      if (!id) throw new Error('No generation ID')
      const result = await getGenerationStatus(id)
      return unwrapResult(result)
    },
    enabled: id !== null,
    refetchInterval: 5_000,
  })
}
